import React, { useState } from 'react';
import { clients } from '../mock';
import { Building2 } from 'lucide-react';
import BackToTop from './BackToTop';

const LogoTile = ({ client }) => (
  <div className="relative flex items-center gap-4">
    <div
      className="w-14 h-14 rounded-sm flex items-center justify-center font-display text-xl text-white tracking-wide"
      style={{ backgroundColor: client.accent || '#0c1a2b' }}
    >
      {client.short}
    </div>
    <div>
      <div className="font-display text-xl text-[#0c1a2b] leading-tight">{client.name}</div>
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#5b6b7d] mt-1">
        {client.domain.split('·')[0].trim()}
      </div>
    </div>
  </div>
);

const LogoArea = ({ client }) => {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <div className="h-44 flex items-center justify-center px-10 py-8 bg-[#fbf9f4] border-b border-[#0c1a2b]/8 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(12,26,43,0.6) 1px, transparent 1px)',
          backgroundSize: '14px 14px',
        }}
      />
      {!imgFailed ? (
        <img
          src={client.logo}
          alt={`${client.name} logo`}
          className="relative max-h-16 max-w-[80%] object-contain transition-transform duration-500 group-hover:scale-[1.04]"
          loading="lazy"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <LogoTile client={client} />
      )}
    </div>
  );
};

const ClientMeta = ({ client }) => (
  <div className="p-7 flex-1 flex flex-col">
    <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#c9a961]">
      <Building2 size={12} />
      <span>{client.period}</span>
    </div>
    <h3 className="font-display text-2xl text-[#0c1a2b] mt-3">{client.name}</h3>
    <div className="text-xs tracking-[0.18em] uppercase text-[#5b6b7d] mt-1">{client.domain}</div>
    <div className="h-px bg-[#0c1a2b]/10 my-4 w-10 group-hover:w-20 group-hover:bg-[#c9a961] transition-all duration-300" />
    <p className="text-[14px] text-[#3a4d62] leading-relaxed">{client.blurb}</p>
  </div>
);

const ClientCard = ({ client }) => (
  <article className="group relative bg-white border border-[#0c1a2b]/10 hover:border-[#c9a961]/60 transition-all duration-300 flex flex-col">
    <LogoArea client={client} />
    <ClientMeta client={client} />
    <div
      className="h-1 w-full opacity-90"
      style={{ backgroundColor: client.accent || '#c9a961' }}
    />
  </article>
);

const ClientsHeader = () => (
  <div className="max-w-3xl">
    <div className="section-eyebrow">Clients</div>
    <h2 className="font-display text-4xl md:text-5xl text-[#0c1a2b] mt-4 leading-tight">
      Trusted by leading
      <span className="italic text-[#c9a961]"> global financial </span>
      institutions.
    </h2>
    <div className="gold-line mt-6" />
    <p className="text-[#3a4d62] mt-6 max-w-2xl leading-relaxed">
      Over a decade of partnership with three of the world&rsquo;s most demanding banking clients
      &mdash; spanning capital markets, custody and wealth management mandates.
    </p>
  </div>
);

const EngagementTimeline = () => (
  <div className="mt-12 pt-8 border-t border-[#0c1a2b]/10">
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 text-xs text-[#5b6b7d]">
      <span className="tracking-[0.28em] uppercase text-[#0c1a2b]">Engagement Timeline</span>
      <div className="flex-1 h-px bg-[#0c1a2b]/15 hidden sm:block" />
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {clients.map((c) => (
          <span key={c.name} className="inline-flex items-center gap-2">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: c.accent || '#c9a961' }}
            />
            <span className="font-medium text-[#0c1a2b]">{c.name}</span>
            <span className="opacity-60">{c.period}</span>
          </span>
        ))}
      </div>
    </div>
  </div>
);

const Clients = () => (
  <section id="clients" className="bg-paper py-24 lg:py-28 border-t border-[#0c1a2b]/8">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <ClientsHeader />
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {clients.map((c) => (
          <ClientCard key={c.name} client={c} />
        ))}
      </div>
      <EngagementTimeline />
      <BackToTop targetId="clients" label="Top of Clients" variant="light" className="mt-16" />
    </div>
  </section>
);

export default Clients;
